import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ChatMessage from "./ChatMessage";
import { generateAssistantResponse } from "../assistantLogic";
import { sendAssistantMessage } from "../assistantService";

const initialMessages = [
  {
    id: "assistant-welcome",
    sender: "assistant",
    text: "Hi! I am AutoLogg Assistant. Ask, log, diagnose, or plan your maintenance.",
  },
];

const quickActions = [
  {
    label: "Log service",
    prompt: "I changed my oil today at 44,200 miles and it cost $85.",
    autoSend: false,
  },
  {
    label: "What’s due next?",
    prompt: "What maintenance should I do next?",
    autoSend: true,
  },
  {
    label: "Diagnose issue",
    prompt: "My car shakes when braking.",
    autoSend: true,
  },
  {
    label: "Search history",
    prompt: "Show me all brake services.",
    autoSend: true,
  },
  {
    label: "Create reminder",
    prompt: "Remind me to rotate my tires in 6 months.",
    autoSend: false,
  },
];

function AutoLoggAssistant({
  records = [],
  onAddRecord,
  vehicleProfile,
  currentMileage = 0,
  onCreateReminder,
  onCreateIssue,
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleAssistantResponse = (assistantResponse, loadingMessageId) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === loadingMessageId
          ? {
              ...message,
              text: assistantResponse.text,
              isLoading: false,
              actionData: assistantResponse,
            }
          : message
      )
    );
  };

  const handleSend = async (overrideText) => {
    const text = String(overrideText ?? input).trim();
    if (!text) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text,
    };

    const loadingId = `assistant-loading-${Date.now()}`;
    const loadingMessage = {
      id: loadingId,
      sender: "assistant",
      text: "AutoLogg is thinking...",
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);
    setInput(overrideText ? input : "");
    setLoading(true);

    const response = await sendAssistantMessage({
      message: text,
      records,
      vehicleProfile,
      currentMileage,
    });

    handleAssistantResponse(response, loadingId);
    setLoading(false);
  };

  const handleQuickAction = (action) => {
    setInput(action.prompt);
    if (action.autoSend) {
      handleSend(action.prompt);
    }
  };

  const handleClearChat = () => {
    setMessages(initialMessages);
    setInput("");
  };

  const handleConfirmAddRecord = (recordDraft, messageId) => {
    const cost = recordDraft.cost ?? recordDraft.price ?? recordDraft.amount ?? null;
    const newRecord = {
      id: Date.now(),
      service_type: recordDraft.serviceType,
      mileage: recordDraft.mileage,
      date: recordDraft.date,
      cost: cost,
      notes: recordDraft.notes,
      vehicle_id: vehicleProfile?.id ?? null,
      source: "assistant",
    };

    onAddRecord(newRecord);
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              actionData: { ...message.actionData, completed: true },
            }
          : message
      )
    );
    setMessages((prev) => [
      ...prev,
      {
        id: `assistant-confirm-${Date.now()}`,
        sender: "assistant",
        text: "The maintenance entry has been added to your records.",
      },
    ]);
  };

  const handleConfirmReminder = async (reminderDraft, messageId) => {
    let savedSuccessfully = false;

    if (onCreateReminder) {
      try {
        await onCreateReminder(reminderDraft);
        savedSuccessfully = true;
      } catch (error) {
        console.error("Failed to save reminder from assistant:", error);
      }
    }

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              actionData: { ...message.actionData, completed: true },
            }
          : message
      )
    );
    setMessages((prev) => [
      ...prev,
      {
        id: `assistant-reminder-${Date.now()}`,
        sender: "assistant",
        text: savedSuccessfully
          ? `Reminder saved.${reminderDraft.dueDate ? ` Due ${reminderDraft.dueDate}.` : reminderDraft.dueMileage ? ` Due at ${reminderDraft.dueMileage} miles.` : ""}`
          : "I attempted to save the reminder, but there was an issue. Please try again.",
      },
    ]);
  };

  const handleConfirmIssue = (issueDraft, messageId) => {
    if (onCreateIssue) {
      onCreateIssue(issueDraft);
    }

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              actionData: { ...message.actionData, completed: true },
            }
          : message
      )
    );
    setMessages((prev) => [
      ...prev,
      {
        id: `assistant-issue-${Date.now()}`,
        sender: "assistant",
        text: "Issue log created. Keep an eye on your vehicle and update this entry when you have more details.",
      },
    ]);
  };

  const handleCancelAction = (messageId) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              actionData: null,
            }
          : message
      )
    );
  };

  return (
    <Box>
      {!open && (
        <IconButton
          aria-label="Open AutoLogg Assistant"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1500,
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            boxShadow: theme.shadows[8],
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <ChatBubbleOutlineIcon />
        </IconButton>
      )}

      {open && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1500,
            width: { xs: "calc(100vw - 32px)", sm: 380 },
            height: { xs: "75vh", sm: 560 },
            borderRadius: 3,
            boxShadow: theme.shadows[20],
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, backgroundColor: theme.palette.background.paper }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>AutoLogg Assistant</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Ask, log, diagnose, or plan maintenance.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="Online" size="small" color="success" />
                <IconButton aria-label="Close AutoLogg Assistant" size="small" onClick={() => setOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          <Divider />

          <Box
            ref={chatRef}
            sx={{
              p: 2,
              overflowY: "auto",
              flex: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                onConfirmAddRecord={handleConfirmAddRecord}
                onCreateReminder={handleConfirmReminder}
                onCreateIssue={handleConfirmIssue}
                onCancelAction={handleCancelAction}
              />
            ))}
          </Box>

          <Divider />

          <Box sx={{ p: 1.5, backgroundColor: theme.palette.background.paper }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                placeholder="Ask AutoLogg Assistant..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                fullWidth
                multiline
                maxRows={3}
                size="small"
                disabled={loading}
              />
              <IconButton
                color="primary"
                onClick={() => handleSend()}
                sx={{ alignSelf: "flex-end" }}
                disabled={loading}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {quickActions.map((action) => (
                <Chip
                  key={action.label}
                  label={action.label}
                  onClick={() => handleQuickAction(action)}
                  clickable
                  size="small"
                />
              ))}
            </Box>
            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" onClick={handleClearChat} disabled={loading}>
                Clear chat
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default AutoLoggAssistant;
